import { isPublicAuthPath } from "@/constants/auth-public-routes";

/**
 * Where the Magig SDK is allowed to load.
 *
 * Keep in sync with the blocking script in `index.html`, which has its own
 * copy of these lists (it runs before any module loads, so it cannot import
 * this). `mag-capture-routes.test.ts` compares the two and fails on drift.
 *
 * Three rules, in order:
 *
 * 1. CREDENTIAL routes never load it. These carry a single-use secret in the
 *    query string, and no third-party script should be fetched while one is in
 *    the URL — independent of any redaction the SDK promises.
 * 2. Public auth routes load it: that is where the ad click lands and where
 *    signup_completed fires.
 * 3. `/onboarding` loads it too, so the SSO and deferred-RUC signups — which
 *    complete there, not on /register — are visible to Magig. Everything a
 *    step renders from DGI or the user's own legal data is wrapped in
 *    `data-mag-private`; see the inventory in the test file. NOTE the prefix
 *    deliberately does NOT cover `/accountant/client/:tenantId/onboarding`,
 *    which walks an accountant through a CLIENT's company — a third party's
 *    data and not a signup.
 */
export const MAG_CREDENTIAL_PATH_PREFIXES = [
  "/auth/verify",
  "/auth/reset-password",
  "/accept-invitation",
  "/auth/sso/callback",
] as const;

export const MAG_EXTRA_PATH_PREFIXES = ["/onboarding"] as const;

function matches(pathname: string, prefixes: readonly string[]): boolean {
  return prefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** True when the SDK may load — and, for the route guard, may keep running. */
export function isMagCapturePath(pathname: string): boolean {
  if (matches(pathname, MAG_CREDENTIAL_PATH_PREFIXES)) return false;
  return isPublicAuthPath(pathname) || matches(pathname, MAG_EXTRA_PATH_PREFIXES);
}
